import React, { useEffect, useMemo, useRef, useState } from 'react';
import './GameSetupScreen.css';
import GamePackSelector from './GamePackSelector';
import QuestPackSelector from './QuestPackSelector';
import ContentDetailsModal from './ContentDetailsModal';
import { getGamePackImageSrc } from '../utils/gamePackImages';
import { formatPlayStyle, formatStorePrice, getPackName } from './packSelectorUtils';

const PLAYED_QUEST_PACKS_KEY = 'cq_played_quest_packs';
const CTA_ATTENTION_DURATION_MS = 1600;

const GAME_PRESENTATION = {
  'cringe quest': {
    description: 'Outrageous. Awkward. Always a hit.',
    playerRange: '3-10 Players',
    packCount: '8 Packs',
    recommended: true,
  },
  'after dark': {
    description: 'NSFW. Not for Grandma.',
    playerRange: '2-10 Players',
    packCount: '6 Packs',
  },
  'snack attack trivia': {
    description: 'Trivia, snacks, and savage facts.',
    playerRange: '2-8 Players',
    packCount: '5 Packs',
  },
};

const QUEST_PACK_PRESENTATION = {
  'late night regrets': {
    description: 'Fresh chaos. No filter.',
    tags: ['Spicy', 'Adult', 'Chaotic'],
  },
  'friends gone wild': {
    description: 'Your group chat, in real life.',
    tags: ['Chaotic', 'Adult', 'Party'],
  },
  'cringe quest starter': {
    description: 'The essential Cringe Quest experience.',
    tags: ['Classic', 'Clean-ish', 'All Ages'],
    starter: true,
  },
  'snack attack trivia': {
    description: 'Test your knowledge. Earn bragging rights.',
    tags: ['Trivia', 'Fun', 'All Ages'],
  },
  'after dark': {
    description: 'You played before. Last played 4 days ago.',
    tags: ['NSFW', 'Adult', 'Spicy'],
  },
  'roast royale': {
    description: 'Take the throne of shade.',
    tags: ['Roast', 'Adult', 'Chaotic'],
  },
  'holy cow': {
    description: 'Udderly ridiculous fun.',
    tags: ['Clean-ish', 'Loud', 'All Ages'],
  },
  'worst dates ever': {
    description: 'We all been there.',
    tags: ['Spicy', 'Adult', 'Chaotic'],
  },
};

const normalizeKey = (value) => String(value || '').trim().toLowerCase();
const formatQuestionCount = (value) => {
  const questionCount = Number(value);

  if (!Number.isFinite(questionCount) || questionCount < 0) {
    return 'Questions unavailable';
  }

  const roundedQuestionCount = Math.round(questionCount);
  return `${roundedQuestionCount} ${roundedQuestionCount === 1 ? 'Question' : 'Questions'}`;
};

const readPlayedQuestPackIds = () => {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const stored = JSON.parse(window.localStorage.getItem(PLAYED_QUEST_PACKS_KEY) || '[]');
    return new Set(Array.isArray(stored) ? stored : []);
  } catch (error) {
    return new Set();
  }
};

const sortQuestPacks = (left, right) => {
  const getSortValue = (pack) => {
    if (pack.is_owned && pack.is_unplayed) return 1;
    if (pack.is_owned && pack.is_played) return 2;
    if (pack.is_locked) return 3;
    return 4;
  };

  const delta = getSortValue(left) - getSortValue(right);
  if (delta !== 0) {
    return delta;
  }

  if (left.is_recommended && !right.is_recommended) {
    return -1;
  }

  if (!left.is_recommended && right.is_recommended) {
    return 1;
  }

  return getPackName(left).localeCompare(getPackName(right));
};

const GameSetupScreen = ({
  gamePacks,
  selectedGamePack,
  onSelectGamePack,
  selectedGamePackRequiresQuestPack,
  questPacks,
  selectedQuestPack,
  onSelectQuestPack,
  questPackLoading,
  questPackError,
  readyToStart,
  loading,
  onProceed,
  onOpenSettings,
  onOpenCreditStore,
  onPurchaseContent,
  creditBalance = 0,
  purchasingItemId = null,
}) => {
  const [step, setStep] = useState(selectedGamePack ? 'quest' : 'game');
  const [detailState, setDetailState] = useState({ pack: null, type: null });
  const [playedQuestPackIds, setPlayedQuestPackIds] = useState(() => readPlayedQuestPackIds());
  const [isPrimaryCtaAnimating, setIsPrimaryCtaAnimating] = useState(false);
  const ctaAttentionTimeoutRef = useRef(null);
  const ctaAttentionFrameRef = useRef(null);
  const hasAnimatedStepRef = useRef({ game: false, quest: false });
  const lastAnimatedSelectionRef = useRef({ game: null, quest: null });
  const stepRef = useRef(step);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const triggerPrimaryCtaAttention = () => {
    if (ctaAttentionTimeoutRef.current) {
      window.clearTimeout(ctaAttentionTimeoutRef.current);
      ctaAttentionTimeoutRef.current = null;
    }

    if (ctaAttentionFrameRef.current) {
      window.cancelAnimationFrame(ctaAttentionFrameRef.current);
      ctaAttentionFrameRef.current = null;
    }

    setIsPrimaryCtaAnimating(false);

    ctaAttentionFrameRef.current = window.requestAnimationFrame(() => {
      setIsPrimaryCtaAnimating(true);
      ctaAttentionTimeoutRef.current = window.setTimeout(() => {
        setIsPrimaryCtaAnimating(false);
        ctaAttentionTimeoutRef.current = null;
      }, CTA_ATTENTION_DURATION_MS);
    });
  };

  useEffect(() => {
    setPlayedQuestPackIds(readPlayedQuestPackIds());
  }, []);

  useEffect(() => () => {
    if (ctaAttentionTimeoutRef.current) {
      window.clearTimeout(ctaAttentionTimeoutRef.current);
    }

    if (ctaAttentionFrameRef.current) {
      window.cancelAnimationFrame(ctaAttentionFrameRef.current);
    }
  }, []);

  useEffect(() => {
    if (!selectedGamePack) {
      setStep('game');
    }
  }, [selectedGamePack]);

  useEffect(() => {
    if (selectedGamePack && selectedQuestPack) {
      setStep('quest');
    }
  }, [selectedGamePack, selectedQuestPack]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleDeviceBack = () => {
      if (stepRef.current === 'quest') {
        setDetailState({ pack: null, type: null });
        setStep('game');
      }
    };

    window.addEventListener('popstate', handleDeviceBack);
    return () => {
      window.removeEventListener('popstate', handleDeviceBack);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || step !== 'quest') {
      return;
    }

    if (window.history.state?.cqSetupStep === 'quest') {
      return;
    }

    window.history.pushState(
      {
        ...(window.history.state || {}),
        cqSetupStep: 'quest',
      },
      document.title,
      window.location.href
    );
  }, [step]);

  useEffect(() => {
    if (step !== 'game' || selectedGamePack || !(gamePacks || []).length) {
      return;
    }

    const firstPlayableGame = gamePacks.find((pack) => !pack.is_locked) || gamePacks[0] || null;
    if (firstPlayableGame?.id) {
      onSelectGamePack?.(firstPlayableGame.id);
    }
  }, [gamePacks, onSelectGamePack, selectedGamePack, step]);

  const decoratedGamePacks = useMemo(
    () => (gamePacks || []).map((pack, index) => {
      const presentation = GAME_PRESENTATION[normalizeKey(pack.name)] || {};
      const isRecommended = Boolean(presentation.recommended || (!pack.is_locked && index === 0));

      return {
        ...pack,
        is_recommended: Boolean(pack.is_recommended || isRecommended),
        display_description: pack.description || presentation.description || 'Pick a game fast, then choose a fresh Quest Pack.',
        player_range: presentation.playerRange || '3-10 Players',
        pack_count_label: presentation.packCount || `${pack.include_questions ? 'Quest Packs' : 'Ready to Play'}`,
        play_style_label: formatPlayStyle(pack.play_style),
      };
    }),
    [gamePacks]
  );

  const selectedGamePackDetails = decoratedGamePacks.find((pack) => pack.id === selectedGamePack) || null;

  const orderedGamePacks = useMemo(
    () => [...decoratedGamePacks].sort((left, right) => {
      const getSortValue = (pack) => {
        if (pack.is_owned && !pack.is_locked) return 1;
        if (pack.is_locked && pack.purchasable_item_id) return 2;
        return 3;
      };

      const delta = getSortValue(left) - getSortValue(right);
      if (delta !== 0) {
        return delta;
      }

      if (left.is_recommended && !right.is_recommended) {
        return -1;
      }

      if (!left.is_recommended && right.is_recommended) {
        return 1;
      }

      return getPackName(left).localeCompare(getPackName(right));
    }),
    [decoratedGamePacks]
  );

  const decoratedQuestPacks = useMemo(
    () => (questPacks || [])
      .map((pack, index) => {
        const presentation = QUEST_PACK_PRESENTATION[normalizeKey(pack.name)] || {};
        const hasPlayed = Boolean(pack.is_played || playedQuestPackIds.has(pack.id));
        const isOwned = Boolean(pack.is_owned && !pack.is_locked);
        const isUnplayed = isOwned && !hasPlayed;

        return {
          ...pack,
          tags: (pack.tags?.length ? pack.tags : presentation.tags || []).slice(0, 3),
          display_description: pack.description || presentation.description || 'Add this unplayed pack to your rotation.',
          is_starter: Boolean(pack.is_starter || presentation.starter),
          is_played: hasPlayed,
          is_unplayed: isUnplayed,
          is_recommended: index === 0 && isOwned,
        };
      })
      .sort(sortQuestPacks),
    [playedQuestPackIds, questPacks]
  );

  useEffect(() => {
    if (
      step !== 'quest' ||
      !selectedGamePackRequiresQuestPack ||
      selectedQuestPack ||
      !decoratedQuestPacks.length
    ) {
      return;
    }

    const firstNewPack = decoratedQuestPacks.find((pack) => pack.is_owned && pack.is_unplayed && !pack.is_locked);
    const fallbackPack = decoratedQuestPacks.find((pack) => !pack.is_locked) || decoratedQuestPacks[0];
    const nextPack = firstNewPack || fallbackPack;

    if (nextPack?.id) {
      onSelectQuestPack?.(nextPack.id);
    }
  }, [
    decoratedQuestPacks,
    onSelectQuestPack,
    selectedGamePackRequiresQuestPack,
    selectedQuestPack,
    step,
  ]);

  const allPremiumQuestPacksOwned = Boolean(
    decoratedQuestPacks.length &&
    decoratedQuestPacks.some((pack) => pack.price_usd != null) &&
    decoratedQuestPacks.every((pack) => !pack.is_locked)
  );

  const detailContent = useMemo(() => {
    if (!detailState.pack) {
      return null;
    }

    const pack = detailState.pack;
    const isQuestPack = detailState.type === 'quest_pack';
    const stats = isQuestPack
      ? [
          { label: 'Questions', value: formatQuestionCount(pack.question_count) },
        ]
      : [
          { label: 'Players', value: pack.player_range || '3-10 Players' },
          { label: 'Quest Packs', value: pack.pack_count_label || 'Ready to play' },
        ];

    return {
      pack,
      eyebrow: isQuestPack ? 'Quest Pack Preview' : 'Game Preview',
      title: getPackName(pack),
      description: pack.display_description || pack.description,
      tags: (pack.tags || []).slice(0, 3),
      stats,
      callout: isQuestPack
        ? null
        : {
            label: 'Quick Start',
            copy: 'Choose this game fast, then pick a fresh Quest Pack.',
          },
      priceLabel: formatStorePrice(pack.price_usd) || 'Premium unlock',
      ctaLabel: isQuestPack ? 'Unlock Pack' : 'Unlock Game',
      isLocked: Boolean(pack.is_locked),
      canPurchase: Boolean(pack.purchasable_item_id),
      isPurchasing: Boolean(pack.purchasable_item_id && purchasingItemId === pack.purchasable_item_id),
    };
  }, [detailState.pack, detailState.type, purchasingItemId]);

  const handleSelectGame = async (gamePackId) => {
    await onSelectGamePack?.(gamePackId);
  };

  const handleChoosePack = () => {
    if (!selectedGamePackDetails) {
      return;
    }

    if (selectedGamePackDetails.is_locked) {
      setDetailState({ pack: selectedGamePackDetails, type: 'game_pack' });
      return;
    }

    if (!selectedGamePackRequiresQuestPack) {
      onProceed?.();
      return;
    }

    setStep('quest');
  };

  const handleChangeGame = () => {
    setStep('game');
  };

  const gameCtaDisabled = !selectedGamePack || loading;
  const questCtaDisabled = !readyToStart || loading;
  const gamePrimaryCtaLabel = step === 'game'
    ? (!selectedGamePackDetails?.is_locked && !selectedGamePackRequiresQuestPack ? 'Start Game' : 'Choose Pack')
    : (loading ? 'Starting...' : 'Start Game');
  const activeSelectionId = step === 'game' ? selectedGamePack : selectedQuestPack;
  const canAnimatePrimaryCta = step === 'game'
    ? Boolean(selectedGamePack && !loading)
    : Boolean(readyToStart && !loading);

  useEffect(() => {
    if (!activeSelectionId || !canAnimatePrimaryCta) {
      return;
    }

    const hasAnimatedCurrentStep = hasAnimatedStepRef.current[step];
    const lastAnimatedSelection = lastAnimatedSelectionRef.current[step];

    if (!hasAnimatedCurrentStep || lastAnimatedSelection !== activeSelectionId) {
      triggerPrimaryCtaAttention();
    }

    hasAnimatedStepRef.current[step] = true;
    lastAnimatedSelectionRef.current[step] = activeSelectionId;
  }, [activeSelectionId, canAnimatePrimaryCta, step]);

  return (
    <>
      <ContentDetailsModal
        visible={Boolean(detailContent)}
        content={detailContent}
        loading={loading}
        onClose={() => setDetailState({ pack: null, type: null })}
        onPurchase={(pack) => onPurchaseContent?.(pack)}
      />

      <section
        className="cq-setup-screen"
        style={{ '--cq-chip-image': 'url(/images/cq-logo-coaster.png)' }}
      >
        <header className={`cq-selection-header${step === 'game' ? ' is-game' : ' is-quest'}`}>
          <div className="cq-selection-header__brand">
            <span className="cq-selection-header__crest cq-chip-badge cq-chip-badge--image-only" aria-hidden="true" />
            <h1 className="cq-selection-header__title">Cringe Quest</h1>
            <span className="cq-credit-chip">
              <span className="cq-credit-chip__label">Credits</span>
              <span className="cq-credit-chip__value">{creditBalance}</span>
            </span>
            {step !== 'game' ? (
              <button
                type="button"
                className="cq-back-button cq-back-button--inline"
                onClick={handleChangeGame}
              >
                <span aria-hidden="true">←</span>
                <span>Back</span>
              </button>
            ) : null}
          </div>

          <div className="cq-selection-header__actions">
            {step === 'game' ? (
              <button
                type="button"
                className="cq-icon-button cq-icon-button--settings"
                onClick={onOpenSettings}
                aria-label="Open settings"
              >
                <img src="/images/settings-icon.png" alt="" className="cq-icon-button__icon" />
              </button>
            ) : null}
          </div>
        </header>

        {step === 'game' ? (
          <>
            <section className="cq-quick-start">
              <div className="cq-quick-start__copy">
                <p className="cq-panel-title">Select Game</p>
                <p className="cq-quick-start__description">
                  Choose your Game. Grab a fresh Quest Pack. Let the chaos begin.
                </p>
              </div>
            </section>

            <GamePackSelector
              gamePacks={orderedGamePacks}
              selectedGamePack={selectedGamePack}
              onSelectGamePack={handleSelectGame}
              onOpenLockedGame={(pack) => setDetailState({ pack, type: 'game_pack' })}
              disabled={loading}
              conversationStarted={false}
            />
          </>
        ) : (
          <>
            <section className="cq-quest-context">
              <div className="cq-quest-context__copy">
                <p className="cq-panel-title">Select Quest Pack</p>
                <p className="cq-quest-context__game">
                  Playing: {selectedGamePackDetails ? getPackName(selectedGamePackDetails) : 'Cringe Quest'}
                </p>
              </div>
              {selectedGamePackDetails ? (
                <span className="cq-quest-context__art-wrap" aria-hidden="true">
                  <img
                    className="cq-quest-context__art"
                    src={getGamePackImageSrc(selectedGamePackDetails.image_filename, selectedGamePackDetails.host_image_filename)}
                    alt=""
                  />
                </span>
              ) : null}
            </section>

            {selectedGamePackRequiresQuestPack ? (
              <>
                <QuestPackSelector
                  questPacks={decoratedQuestPacks}
                  selectedPack={selectedQuestPack}
                  onSelectPack={onSelectQuestPack}
                  onOpenLockedPack={(pack) => setDetailState({ pack, type: 'quest_pack' })}
                  disabled={loading}
                  conversationStarted={false}
                  loading={questPackLoading}
                  errorMessage={questPackError}
                />

                {allPremiumQuestPacksOwned ? (
                  <div className="cq-inline-note" role="status">
                    Every premium Quest Pack for this game is already unlocked.
                  </div>
                ) : null}
              </>
            ) : (
              <div className="cq-quest-pack-feedback" role="status">
                This game brings its own chaos. No Quest Pack needed.
              </div>
            )}
          </>
        )}

        <div className="cq-sticky-action-bar">
          <button
            type="button"
            className="cq-secondary-action"
            onClick={onOpenCreditStore}
            disabled={loading}
          >
            Buy Credits
          </button>

          <button
            type="button"
            className={`cq-primary-action${isPrimaryCtaAnimating ? ' is-attention-grab' : ''}`}
            onClick={step === 'game' ? handleChoosePack : onProceed}
            disabled={step === 'game' ? gameCtaDisabled : questCtaDisabled}
          >
            <span className="cq-primary-action__coin cq-chip-badge cq-chip-badge--image-only" aria-hidden="true" />
            <span>{gamePrimaryCtaLabel}</span>
          </button>
        </div>

      </section>
    </>
  );
};

export default GameSetupScreen;
